import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import kycService from '../services/kyc';
import toast from 'react-hot-toast';
import { Button } from '../components/new-components/ui/button';
import { BRAND_ASSETS, TENANT } from '../config';

/**
 * KYCSuccess Component
 * 
 * Handles the redirect from Stripe Identity verification flow.
 * Parses session_id from query params, fetches verification status,
 * updates user profile, and redirects to dashboard.
 */
const KYCSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useUser();
  const [status, setStatus] = useState<'loading' | 'success' | 'processing' | 'error'>('loading');
  const [message, setMessage] = useState('Verifying your identity...');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    let cancelled = false;
    let redirectTimer: ReturnType<typeof setTimeout>;

    // Schedule a redirect that no-ops if the user has already navigated away, so a stale
    // timer can't yank them off whatever page they moved to.
    const go = (path: string, ms: number) => {
      redirectTimer = setTimeout(() => { if (!cancelled) navigate(path); }, ms);
    };

    console.log('🚀 KYCSuccess: Component mounted', {
      sessionId,
      timestamp: new Date().toISOString()
    });

    if (!sessionId) {
      console.error('❌ No session_id in URL');
      setStatus('error');
      setMessage('No verification session found');
      toast.error('Invalid verification link');
      go('/profile', 3000);
      return () => { cancelled = true; clearTimeout(redirectTimer); };
    }

    const fetchVerificationStatus = async () => {
      try {
        console.log('🔍 Fetching verification status for session:', sessionId);
        
        // Fetch the session details from backend
        const response = await kycService.getSessionDetails(sessionId);
        if (cancelled) return;

        console.log('📡 Backend response:', {
          success: response.success,
          hasData: !!response.data,
          response: response
        });

        if (response.success && response.data) {
          const verification = (response.data as any).verification;
          
          if (!verification) {
            console.error('❌ No verification data in response');
            throw new Error('No verification data found');
          }
          
          const verificationStatus = verification.status;

          console.log('✅ Verification status:', {
            status: verificationStatus,
            verifiedAt: verification.verifiedAt,
            createdAt: verification.createdAt,
            updatedAt: verification.updatedAt
          });

          if (verificationStatus === 'verified') {
            console.log('🎉 Verification SUCCESSFUL!');
            setStatus('success');
            setMessage('Identity verification successful! Redirecting to dashboard...');
            toast.success('KYC verification complete!');
            
            console.log('🔄 Refreshing user profile...');
            // Refresh user profile to get updated KYC status
            await refreshUser();
            if (cancelled) return;
            console.log('✅ User profile refreshed');

            // Redirect after 3 seconds
            console.log('➡️ Redirecting to investor dashboard in 3 seconds');
            go('/investor-dashboard', 3000);
          } else if (verificationStatus === 'processing') {
            console.log('⏳ Verification still PROCESSING');
            setStatus('processing');
            setMessage('Your verification is being processed. This may take a few minutes.');

            // Refresh user data
            await refreshUser();
            if (cancelled) return;

            // Redirect after 5 seconds
            go('/profile', 5000);
          } else if (verificationStatus === 'requires_input' || verificationStatus === 'canceled') {
            console.warn('⚠️ Verification incomplete:', verificationStatus);
            setStatus('error');
            setMessage('Verification incomplete. Please try again from your profile.');
            toast.error('KYC verification not completed');

            go('/profile', 3000);
          } else {
            console.error('❌ Verification failed with status:', verificationStatus);
            setStatus('error');
            setMessage('Verification failed. Please contact support or try again.');
            toast.error('KYC verification failed');

            go('/profile', 3000);
          }
        } else {
          console.error('❌ Failed response from backend:', response);
          throw new Error('Failed to fetch verification status');
        }
      } catch (error) {
        if (cancelled) return;
        console.error('❌ Error fetching KYC status:', error);
        setStatus('error');
        setMessage('Error checking verification status. Redirecting to profile...');
        toast.error('Error checking KYC status');

        // Still try to refresh user profile
        try {
          await refreshUser();
        } catch (e) {
          console.error('❌ Failed to refresh user:', e);
        }
        if (cancelled) return;

        go('/profile', 3000);
      }
    };

    fetchVerificationStatus();
    return () => { cancelled = true; clearTimeout(redirectTimer); };
  }, [searchParams, navigate, refreshUser]);

  return (
    <div className="min-h-screen bg-background flex flex-col portal-typography">
      {/* Header — centered logo */}
      <div className="px-4 md:px-6 pt-4 md:pt-6 pb-3 flex justify-center">
        <Link to="/" className="hover:opacity-80 transition-opacity">
          <img
            src={BRAND_ASSETS.logo.primary}
            alt={TENANT.branding.name}
            className="h-11 object-contain"
          />
        </Link>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-6 pb-16">
        <div className="tab-panel-in max-w-xl w-full text-center">
          {status === 'loading' && (
            <div className="space-y-3">
              <RefreshCw className="w-10 h-10 mx-auto text-muted-foreground animate-spin" aria-hidden="true" />
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-medium [text-wrap:balance]">
                Verifying your identity
              </h1>
              <p className="text-base text-muted-foreground [text-wrap:balance]">{message}</p>
            </div>
          )}

          {status === 'success' && (
            <>
              <div className="space-y-3">
                <CheckCircle2 className="w-12 h-12 mx-auto text-[hsl(var(--success))]" aria-hidden="true" />
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-medium [text-wrap:balance]">
                  Identity verified
                </h1>
                <p className="text-lg text-foreground [text-wrap:balance]">{message}</p>
              </div>
              <div className="mt-8 inline-flex items-center gap-2 text-sm text-muted-foreground">
                <RefreshCw className="w-4 h-4 animate-spin" aria-hidden="true" />
                Redirecting…
              </div>
            </>
          )}

          {status === 'processing' && (
            <div className="space-y-3">
              <RefreshCw className="w-12 h-12 mx-auto text-primary animate-spin" aria-hidden="true" />
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-medium [text-wrap:balance]">
                Verification processing
              </h1>
              <p className="text-base text-muted-foreground [text-wrap:balance]">{message}</p>
              <p className="text-sm text-muted-foreground">
                We'll notify you once your verification is complete.
              </p>
            </div>
          )}

          {status === 'error' && (
            <>
              <div className="space-y-3">
                <XCircle className="w-12 h-12 mx-auto text-destructive" aria-hidden="true" />
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-medium [text-wrap:balance]">
                  Verification issue
                </h1>
                <p className="text-base text-muted-foreground [text-wrap:balance]">{message}</p>
              </div>
              <div className="mt-8 max-w-md mx-auto">
                <Button onClick={() => navigate('/profile')} className="w-full h-14 text-lg">
                  Go to settings
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default KYCSuccess;
